/**
 * reCAPTCHA v3 support for the forms that post to forms.quantstamp.com.
 *
 * Every `/submit/*` route on the form handler runs `recaptcha.middleware.verify`
 * (express-recaptcha v3), which reads the token from the `g-recaptcha-response`
 * field of the POST body — so each form has to attach one.
 *
 * The site key is public by design: it is embedded in the page so the browser
 * can request a token. Only the secret key, held by the form handler, can
 * validate one.
 */

/** Field name express-recaptcha reads from the request body. Do not rename. */
export const RECAPTCHA_TOKEN_FIELD = "g-recaptcha-response";

export const RECAPTCHA_SITE_KEY =
  process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || "6LcdTugUAAAAALvQfRjxp2sH2VPm4IPnRO5TIue5";

export const RECAPTCHA_SCRIPT_SRC = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;

/** Actions are reported in the reCAPTCHA admin console, one per form. */
export const RECAPTCHA_ACTIONS = {
  audit: "audit_request",
  pentest: "infra_request",
  analysis: "analysis_request",
  incident: "incident_request",
} as const;

type Grecaptcha = {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
};

declare global {
  interface Window {
    grecaptcha?: Grecaptcha;
  }
}

let scriptLoad: Promise<void> | null = null;

/**
 * Injects the reCAPTCHA script, once per page load.
 *
 * This used to sit in the root layout, so every visitor paid for it — including
 * the overwhelming majority who never open a form. Google's script sets its own
 * cookies and pulls in an `api2/anchor` iframe that sets several more, which is
 * what a Lighthouse third-party-cookie audit counts on every single page.
 *
 * Loading it when a form is actually engaged instead keeps those cookies off
 * the other pages entirely. Call this as early in the interaction as possible —
 * on modal open or field focus, not at submit — because v3 scores a visitor by
 * watching them, and a script that lands at submit time has nothing to go on.
 *
 * Rejection is never surfaced: a blocked or failed script leaves `grecaptcha`
 * undefined, and `getRecaptchaToken` already treats that as "no token".
 */
export function loadRecaptcha(): Promise<void> {
  if (typeof document === "undefined") return Promise.resolve();
  if (scriptLoad) return scriptLoad;

  scriptLoad = new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = RECAPTCHA_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => resolve();
    document.head.appendChild(script);
  });

  return scriptLoad;
}

/** `grecaptcha` shows up some time after the script element finishes loading. */
function whenReady(timeoutMs: number): Promise<Grecaptcha | null> {
  return new Promise((resolve) => {
    const started = Date.now();

    const poll = () => {
      const grecaptcha = window.grecaptcha;
      if (grecaptcha?.execute) {
        grecaptcha.ready(() => resolve(grecaptcha));
        return;
      }
      if (Date.now() - started >= timeoutMs) {
        resolve(null);
        return;
      }
      window.setTimeout(poll, 100);
    };

    poll();
  });
}

/**
 * Resolves to a token, or `null` if reCAPTCHA is unavailable (blocked by an
 * extension, offline, script failed). Callers submit anyway rather than
 * trapping a legitimate user behind a script that never loaded — scoring and
 * enforcement are the form handler's job.
 */
export async function getRecaptchaToken(action: string, timeoutMs = 5000): Promise<string | null> {
  if (typeof window === "undefined") return null;

  try {
    // No-op when a form already warmed the script; covers the callers that did not.
    await loadRecaptcha();
    const grecaptcha = await whenReady(timeoutMs);
    if (!grecaptcha) return null;
    return await grecaptcha.execute(RECAPTCHA_SITE_KEY, { action });
  } catch {
    return null;
  }
}

/**
 * Builds the POST body for a form handler submission, with a reCAPTCHA token
 * attached when one could be obtained.
 *
 * The body has to be url-encoded. The handler mounts only `bodyParser.json()`
 * and `bodyParser.urlencoded()`, so handing `fetch` a `FormData` sends
 * multipart/form-data, which no parser claims — `req.body` stays empty and the
 * submission comes back 400 with every required field reported missing.
 */
export async function buildSubmissionBody(formData: FormData, action: string): Promise<URLSearchParams> {
  const body = new URLSearchParams();
  for (const [field, value] of formData) {
    // Skips the File entries a url-encoded body cannot carry; these forms have none.
    if (typeof value === "string") body.append(field, value);
  }

  const token = await getRecaptchaToken(action);
  if (token) body.set(RECAPTCHA_TOKEN_FIELD, token);
  return body;
}
